/**
 * V2 header proximity light — "something you wanted to go to is close."
 *
 * Follows the bell's contract (alerts.js): the button IS the indicator.
 * It renders only while ≥1 Want-to-go place is within NEARBY_RANGE_KM
 * of a real GPS fix — never the map's panned center. Tapping it opens
 * the Want to go list's map centered on that fix (riding the shared-link
 * ?lat/?lng/?zoom viewport), so the nearby pins are simply in view.
 *
 * It must NEVER be the thing that pops the iOS permission dialog: app
 * chrome asking for location on boot is obnoxious. So it looks for a fix
 * only once it knows one will come silently — the breadcrumb a successful
 * fix leaves (markGeoGranted), or failing that permissions.query. Until
 * some map/nearby feature earns that grant, the light stays dark.
 *
 * Cadence: on boot, on the tab becoming visible again — the "landed in a
 * new city, pulled the app up" moment — and the first time anything in
 * the app lands a fix, throttled otherwise to the fix cache's five
 * minutes. A membership change (save, remove, cross-off after logging a
 * yoink) recounts against the last fix immediately.
 *
 * On-device diagnosis, since the reader tests on an iPad with no console:
 * `?neardiag=1` prints every gate above as it is evaluated.
 */
import { icon } from './icons.js';
import { getUserLocation, onGeoGranted, GEO_GRANTED_KEY } from './map-view.js';
import { fetchSavedPlaces, savedNearby, WANT_TO_GO } from '../features/lists/lists.js';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // matches getUserLocation's maximumAge
const COUNT_CAP = 9;

/**
 * A zoom that keeps every counted pin inside the viewport when the map
 * opens centered on the fix. A fixed city zoom cropped in-range pins
 * (2026-07-27 on-device: badge said 2, map showed 1). Opening a notch
 * too wide is fine; hiding a pin the badge promised is the bug. Pure;
 * exported for tests.
 */
export function zoomToFit(farthestKm, lat, halfSpanPx = 160) {
  // Web-mercator meters per pixel at zoom z: 156543·cos(lat)/2^z. Keep
  // the farthest pin (plus margin) inside the given half-span.
  const halfSpanMeters = 156543.03 * Math.cos((lat * Math.PI) / 180) * halfSpanPx;
  const neededMeters = Math.max(farthestKm, 0.5) * 1000 * 1.3;
  const z = Math.floor(Math.log2(halfSpanMeters / neededMeters));
  return Math.min(13, Math.max(8, z));
}

/**
 * The tap happens on the device that will render the map, so measure
 * it instead of assuming the worst everywhere (which framed ~200km for
 * a ~35km spread on the iPad's big map). 35% of the screen's short
 * side ≈ half the map's smaller dimension across our layouts; the
 * 160px floor keeps the small-phone guarantee.
 */
export function viewportHalfSpanPx() {
  return Math.max(160, Math.min(window.innerWidth, window.innerHeight) * 0.35);
}

/** Never prompts — resolves true only when a fix would come silently. */
async function locationGranted() {
  // The breadcrumb is checked FIRST, and on its own is enough. A previous
  // successful fix means the browser handed one over without prompting,
  // which is the exact condition this gate is testing for.
  //
  // It used to be the fallback, reached only when permissions.query threw
  // — and that is why the light went dark (2026-08-06 report: "my alert
  // for nearby want-to-go seems to be gone"). iOS Safari answers 'prompt'
  // for geolocation until the page takes a fix IN THAT session, so the
  // gate said no on boot, burned its throttle, and never asked again —
  // while Discover's own locate call succeeded a second later and drew
  // the map around the reader. Nothing was wrong with the count; the
  // light simply never got to run.
  try {
    if (localStorage.getItem(GEO_GRANTED_KEY) === '1') return true;
  } catch { /* private mode — fall through to the Permissions API */ }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false; // no Permissions API and no breadcrumb: stay conservative
  }
}

/**
 * `?neardiag=1` — the light's own on-page readout, same pattern as
 * ?authdiag=1. Every gate this module passes through, in order, with the
 * value that decided it. A dark light has six possible reasons and none
 * of them are visible from an iPad; this is how the reader tells us which
 * one it was. Returns a no-op logger when the flag is absent.
 */
function makeDiag() {
  let panel = null;
  try {
    if (!new URLSearchParams(window.location.search).has('neardiag')) return () => {};
  } catch { return () => {}; }
  return (line) => {
    if (!panel) {
      panel = document.createElement('details');
      panel.id = 'near-diag-panel';
      panel.open = true;
      panel.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:9600;'
        + 'background:#1b1b16;color:#e8e2cf;font:11px/1.5 monospace;padding:8px;'
        + 'border-radius:8px;max-height:45vh;overflow:auto;opacity:.95';
      panel.innerHTML = '<summary>nearby want-to-go diagnostics</summary><pre style="white-space:pre-wrap;margin:4px 0"></pre>';
      document.body.appendChild(panel);
    }
    const pre = panel.querySelector('pre');
    pre.textContent += `${line}\n`;
  };
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  let destroyed = false;
  let checking = false;
  let lastCheck = 0;
  let here = null; // last GPS fix
  let nearby = []; // want-to-go entries in range, nearest first
  const diag = makeDiag();

  el.innerHTML = `
    <button type="button" class="v2-alerts-btn v2-near-btn" data-near-open hidden>
      ${icon('pin', 22)}
      <span class="v2-alerts-count v2-near-count" aria-hidden="true"></span>
    </button>`;
  const btn = el.querySelector('[data-near-open]');
  const countEl = el.querySelector('.v2-near-count');

  function paint() {
    const count = nearby.length;
    // The tap needs a username to build the list URL; profile settles
    // right after auth, and the light can wait that beat out.
    if (!count || !store.get().user || !store.get().profile?.username) {
      btn.hidden = true;
      return;
    }
    countEl.textContent = count > COUNT_CAP ? `${COUNT_CAP}+` : String(count);
    btn.setAttribute(
      'aria-label',
      `${count} saved place${count === 1 ? '' : 's'} near you — open the map`
    );
    btn.hidden = false;
  }

  /** Recount against the last fix (membership changed; no GPS needed). */
  async function recount() {
    if (!here || !store.get().user) return;
    try {
      const entries = await fetchSavedPlaces(store);
      if (destroyed) return;
      nearby = savedNearby(entries, here);
      paint();
    } catch { /* keep the last honest count */ }
  }

  async function check(force = false) {
    if (checking || destroyed) return;
    if (!force && Date.now() - lastCheck < CHECK_INTERVAL_MS) return;
    if (!store.get().user) {
      diag('check: signed out — nothing to count');
      nearby = [];
      paint();
      return;
    }
    checking = true;
    lastCheck = Date.now();
    try {
      const granted = await locationGranted();
      diag(`check(force=${force}): location gate ${granted ? 'OPEN' : 'SHUT — light stays dark'}`);
      if (!granted) return;
      const fix = await getUserLocation();
      diag(`  fix: ${fix ? `${fix.lat.toFixed(4)},${fix.lng.toFixed(4)}` : 'none (denied or timed out)'}`);
      if (destroyed || !fix) return;
      here = fix;
      const entries = await fetchSavedPlaces(store);
      if (destroyed) return;
      nearby = savedNearby(entries, here);
      diag(`  ${entries.length} saved place(s), ${nearby.length} within range`
        + `${nearby.length ? ` (nearest ${nearby[0].km.toFixed(1)}km)` : ''}`);
      paint();
      diag(`  light: ${!btn.hidden ? 'lit' : nearby.length
        ? 'dark — counted, but the profile has no username to link to yet' : 'dark'}`);
    } catch (err) {
      // Offline or the lists schema missing — a dark light, not an error
      diag(`  failed: ${err?.message || err}`);
    } finally {
      checking = false;
    }
  }

  function onClick(event) {
    if (!event.target.closest('[data-near-open]')) return;
    const username = store.get().profile?.username;
    if (!username || !here) return;
    // nearby is sorted nearest-first, so the last entry is the farthest
    // pin the badge counted — the one the viewport must not crop.
    const farthestKm = nearby[nearby.length - 1]?.km ?? 0;
    router.navigate('list', { username, slug: WANT_TO_GO.slug }, {
      query: {
        view: 'map',
        lat: here.lat.toFixed(5),
        lng: here.lng.toFixed(5),
        zoom: String(zoomToFit(farthestKm, here.lat, viewportHalfSpanPx())),
      },
    });
  }

  function onVisibility() {
    if (document.visibilityState === 'visible') check();
  }

  // Auth settles after first paint; saves/cross-offs move the count.
  let seenUserId = store.get().user?.id || null;
  let seenUsername = store.get().profile?.username || null;
  let seenSaved = store.get().savedLocationIds;
  const unsubscribe = store.subscribe((state) => {
    if (destroyed) return;
    if ((state.user?.id || null) !== seenUserId) {
      seenUserId = state.user?.id || null;
      here = null;
      nearby = [];
      paint();
      check(true);
      return;
    }
    if ((state.profile?.username || null) !== seenUsername) {
      // The profile lands a beat after the user — the count may already
      // be sitting there waiting for a username to link to.
      seenUsername = state.profile?.username || null;
      paint();
    }
    if (state.savedLocationIds !== seenSaved) {
      seenSaved = state.savedLocationIds;
      recount();
    }
  });

  el.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', onVisibility);
  // First visit on this browser: the gate below is shut until something
  // else takes a fix (the feed's own locate call, or the map). Without
  // this the light would sit dark for the rest of the visit.
  const stopGeoWatch = onGeoGranted(() => check(true));
  check();

  return () => {
    destroyed = true;
    unsubscribe();
    stopGeoWatch();
    el.removeEventListener('click', onClick);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
